import { ApiReadLanderResponse, ApiSimpleCampaign } from "@incendium/api";
import {
  Autocomplete,
  Box,
  IconButton,
  InputAdornment,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  Tooltip,
} from "@mui/material";
import { useState, useEffect } from "react";
import ClearIcon from "@mui/icons-material/Clear";
import InsertLinkIcon from "@mui/icons-material/InsertLink";
import BoltIcon from "@mui/icons-material/Bolt";
import CallSplitOutlinedIcon from "@mui/icons-material/CallSplitOutlined";

export const SMART_LINK_TYPE_ICONS = {
  url: InsertLinkIcon,
  lander: BoltIcon,
  campaign: CallSplitOutlinedIcon,
} as const;

interface SmartLinkSelectorProps {
  linkType: "lander" | "url" | "campaign";
  onLinkTypeChange: (type: "lander" | "url" | "campaign") => void;
  selectedLanderId: number | "";
  onLanderChange: (landerId: number | "") => void;
  customUrl: string;
  onCustomUrlChange: (url: string) => void;
  landers: ApiReadLanderResponse[];
  campaigns: ApiSimpleCampaign[];
  selectedCampaignId: number | "";
  onCampaignChange: (campaignId: number | "") => void;
  disabled?: boolean;
}

function SmartLinkSelector({
  linkType,
  onLinkTypeChange,
  selectedLanderId,
  onLanderChange,
  customUrl,
  onCustomUrlChange,
  landers,
  campaigns,
  selectedCampaignId,
  onCampaignChange,
  disabled = false,
}: SmartLinkSelectorProps) {
  const [urlError, setUrlError] = useState<string>("");

  // Validate URL format
  useEffect(() => {
    if (linkType === "url" && customUrl) {
      const domainRegex =
        /https?:\/\/(www.)?[-a-zA-Z0-9@:%._+~#=]{1,256}.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&\/\/=]*)/;
      if (!domainRegex.test(customUrl)) {
        setUrlError("Enter a valid domain URL");
      } else {
        setUrlError("");
      }
    } else {
      setUrlError("");
    }
  }, [customUrl, linkType]);

  // Clear other values when switching link types
  useEffect(() => {
    if (linkType === "lander") {
      onCustomUrlChange("");
      onCampaignChange("");
    } else if (linkType === "url") {
      onLanderChange("");
      onCampaignChange("");
    } else {
      onCustomUrlChange("");
      onLanderChange("");
    }
  }, [linkType]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCustomUrlChange = (url: string) => {
    onCustomUrlChange(url);
    if (url && selectedLanderId) {
      onLanderChange("");
    }
  };

  const handleLanderChange = (landerId: number | "") => {
    onLanderChange(landerId);
    if (landerId && customUrl) {
      onCustomUrlChange("");
    }
  };

  return (
    <Stack direction="row" spacing={2} alignItems="flex-end">
      <ToggleButtonGroup
        value={linkType}
        exclusive
        onChange={(_, newValue) => {
          if (newValue !== null) {
            onLinkTypeChange(newValue);
          }
        }}
        disabled={disabled}
        size="small"
      >
        <Tooltip title="Custom URL" arrow>
          <ToggleButton value="url">
            <InsertLinkIcon fontSize="small" />
          </ToggleButton>
        </Tooltip>
        <Tooltip title="Lightning Lander" arrow>
          <ToggleButton value="lander">
            <BoltIcon fontSize="small" />
          </ToggleButton>
        </Tooltip>
        <Tooltip title="Split Campaign" arrow>
          <ToggleButton value="campaign">
            <CallSplitOutlinedIcon fontSize="small" />
          </ToggleButton>
        </Tooltip>
      </ToggleButtonGroup>

      {linkType === "lander" ? (
        <Autocomplete
          size="small"
          options={landers}
          getOptionLabel={(option) =>
            option.path || option.url || `Lander ${option.id}`
          }
          value={
            selectedLanderId
              ? landers.find((l) => l.id === selectedLanderId) || null
              : null
          }
          onChange={(_, newValue) => {
            handleLanderChange(newValue?.id || "");
          }}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          disabled={disabled}
          sx={{ minWidth: 250 }}
          renderInput={(params) => (
            <TextField {...params} label="Select Lander" />
          )}
          renderOption={(props, option) => (
            <Box component="li" {...props}>
              {option.path || option.url || `Lander ${option.id}`}
            </Box>
          )}
        />
      ) : linkType === "campaign" ? (
        <Autocomplete
          size="small"
          options={campaigns}
          getOptionLabel={(option) => option.name || `Campaign ${option.id}`}
          value={
            selectedCampaignId
              ? campaigns.find((c) => c.id === selectedCampaignId) || null
              : null
          }
          onChange={(_, newValue) => {
            onCampaignChange(newValue?.id || "");
          }}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          disabled={disabled}
          sx={{ minWidth: 250 }}
          renderInput={(params) => (
            <TextField {...params} label="Select Split Campaign" />
          )}
          renderOption={(props, option) => (
            <Box component="li" {...props}>
              {option.name || `Campaign ${option.id}`}
            </Box>
          )}
        />
      ) : (
        <TextField
          size="small"
          variant="outlined"
          label="Custom URL"
          value={customUrl}
          onChange={(e) => handleCustomUrlChange(e.target.value)}
          disabled={disabled}
          sx={{ minWidth: 250 }}
          placeholder="https://example.com"
          error={!!urlError}
          helperText={urlError}
          InputProps={{
            endAdornment: customUrl ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => handleCustomUrlChange("")}
                  disabled={disabled}
                  edge="end"
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
      )}
    </Stack>
  );
}

export default SmartLinkSelector;
